import type { Lead } from './types';

/**
 * Gathering leads under the company they share — as a view, not as the row.
 *
 * ── The measurement this is built on ───────────────────────────────────────
 *
 * 27 August 2026, counted against the live database rather than estimated:
 *
 *   690 leads link to 244 companies, and 235 have no company at all
 *   160 of those 244 companies have EXACTLY ONE LEAD — two thirds of them
 *   the whole 211-row saving comes from the other 84
 *   New 367 → 255 and Contacted 297 → 218, but Engaged 15 → 15,
 *     Converted 9 → 9, Closed 2 → 2
 *
 * So company grouping compresses the untouched backlog and nothing else.
 * Every stage where somebody is actually in a conversation saves zero rows.
 *
 * ── Why this is a toggle and not the row ───────────────────────────────────
 *
 * 29 of the 367 leads awaiting a first touch sit at a company where somebody
 * ELSE has already been contacted. Make company the row and that row reads as
 * worked while 29 people who raised their hand sit invisible behind it —
 * Alex's first requirement, in his words: "every single person who raises
 * their hand should be visible, owned by someone, and clearly worked."
 *
 * Kyu picked the toggle. So the grouping is off by default, somebody turns it
 * on for the job it helps with, and the header row carries the worked count so
 * that even collapsed, the untouched are counted on the row containing them.
 *
 * ── A company with one lead is not a group ─────────────────────────────────
 *
 * 160 of 244 companies. Wrapping those in a header would be a lead row wearing
 * a different label — same person, same count, one more thing to expand. They
 * stay ordinary rows, and so does every lead with no company.
 */

/**
 * 'round' joined in Drop 31: the Rounds view renders through the same LeadGroup
 * machinery so the table has ONE way to draw a grouped list. A round group is
 * always open (a work queue is worked, not collapsed) and is never eligible
 * for the mislink flag — that check asks a question about company records.
 */
export type GroupKind = 'company' | 'lone' | 'round';

export interface LeadGroup {
  /** Stable across renders: the company id, or the lead id for a lone row. */
  key: string;
  kind: GroupKind;
  companyId: string | null;
  /** Blank for a lone row — the lead's own cells already say it. */
  companyName: string;
  leads: Lead[];
  /** Leads here that have been touched at least once. */
  worked: number;
  /** The rest. The number that must not disappear when a group collapses. */
  untouched: number;
}

/**
 * What the group is called — the COMPANY RECORD's name, not a lead's guess.
 *
 * ⚠ THIS WAS WRONG IN THE FIRST VERSION and the data caught it within a day.
 * It used to take the first lead's `company_name` text. One company record
 * holds 19 leads carrying 18 DIFFERENT company names — AFSCME District Council
 * 47, austinartist, Fish Pono, Huron Capital and the rest, all pointing at a
 * record called Celtic Sea Salt. Because Brandon Cornett sorted first, the
 * header read "USHealth Group", which is neither the record nor most of what
 * is inside it.
 *
 * The record's own name is the only honest answer: it is the thing the leads
 * are actually linked to, and a header that disagrees with the company page is
 * worse than no header. The lead's text is the fallback for a record whose
 * name has not loaded, and 'Unnamed company' after that.
 */
function nameOfGroup(leads: Lead[], companyNames: ReadonlyMap<string, string>): string {
  const companyId = leads[0]?.company_id ?? null;
  const onRecord = companyId === null ? undefined : companyNames.get(companyId);
  if (onRecord !== undefined && onRecord.trim() !== '') {
    return onRecord.trim();
  }
  for (const lead of leads) {
    const name = lead.company_name.trim();
    if (name !== '') {
      return name;
    }
  }
  return 'Unnamed company';
}

/**
 * True when the leads inside disagree with each other about the company.
 *
 * Separates a record used as a BUCKET from a company spelled two ways —
 * "Liquid I.V." and "Liquid IV", correctly consolidated, which is the link
 * doing its job and must never be flagged.
 *
 * ── The thresholds, and why they are where they are ────────────────────────
 *
 * ⚠ THE FIRST VERSION OVER-FIRED, caught by turning the toggle on against real
 * data an hour after it shipped. It asked for `names >= leads - 1`, which at
 * three leads means two names trips it — exactly the spelling case. It flagged
 * five records where only one is wrong.
 *
 * Measured against the real twelve:
 *
 *   Celtic Sea Salt  19 leads, 18 names, 0.95   a bucket
 *   KELLANOVA         5 leads,  3 names, 0.60   spellings
 *   Liquid I.V.       6 leads,  2 names, 0.33   spellings
 *   Hain Celestial    3 leads,  2 names         ambiguous at this size
 *   Jackson's Chips   3 leads,  3 names         ambiguous at this size
 *
 * So: at least FOUR leads, and at least four fifths of them naming something
 * different. Three leads with three names could as easily be "Jackson's
 * Chips", "Jacksons Chips" and "Jackson's" — genuinely ambiguous, and a flag
 * that fires on ambiguity is a flag people learn to ignore.
 *
 * A badge that cries wolf on eleven records buries the one that is wrong,
 * which is the whole failure it exists to prevent.
 *
 * Surfaced rather than fixed silently: which of the 19 is really Celtic Sea
 * Salt is a question only a person can answer.
 */
const MISLINK_MIN_LEADS = 4;
const MISLINK_NAME_RATIO = 0.8;

export function looksMislinked(group: LeadGroup): boolean {
  if (group.kind !== 'company' || group.leads.length < MISLINK_MIN_LEADS) {
    return false;
  }
  const names = new Set(
    group.leads.map((lead) => lead.company_name.trim().toLowerCase()).filter((n) => n !== ''),
  );
  return names.size / group.leads.length >= MISLINK_NAME_RATIO;
}

function countWorked(leads: Lead[]): { worked: number; untouched: number } {
  const worked = leads.filter((lead) => lead.touch_count > 0).length;
  return { worked, untouched: leads.length - worked };
}

/**
 * The rows to render, in the order the list already had.
 *
 * ORDER IS PRESERVED, and that matters more than it looks: a company group
 * appears where its FIRST lead appeared. So a list sorted by last touch still
 * reads as a list sorted by last touch, with the company sitting at its most
 * recent member rather than being re-sorted alphabetically underneath the
 * reader.
 */
export function groupLeadsByCompany(
  leads: Lead[],
  /** Company id → the name on the company record. Empty until it loads. */
  companyNames: ReadonlyMap<string, string> = new Map(),
): LeadGroup[] {
  const byCompany = new Map<string, Lead[]>();
  for (const lead of leads) {
    if (lead.company_id === null) {
      continue;
    }
    const held = byCompany.get(lead.company_id);
    if (held === undefined) {
      byCompany.set(lead.company_id, [lead]);
    } else {
      held.push(lead);
    }
  }

  const groups: LeadGroup[] = [];
  const emitted = new Set<string>();

  for (const lead of leads) {
    const companyId = lead.company_id;
    const siblings = companyId === null ? undefined : byCompany.get(companyId);

    if (companyId === null || siblings === undefined || siblings.length < 2) {
      const counts = countWorked([lead]);
      groups.push({
        key: lead.id,
        kind: 'lone',
        companyId,
        companyName: '',
        leads: [lead],
        ...counts,
      });
      continue;
    }

    if (emitted.has(companyId)) {
      continue;
    }
    emitted.add(companyId);
    groups.push({
      key: companyId,
      kind: 'company',
      companyId,
      companyName: nameOfGroup(siblings, companyNames),
      leads: siblings,
      ...countWorked(siblings),
    });
  }

  return groups;
}

/** How many rows the table draws with every company group collapsed. */
export function collapsedRowCount(groups: LeadGroup[]): number {
  return groups.length;
}

/**
 * What a collapsed company row says about the people inside it.
 *
 * Never a single worked / not-worked state. That is the entire mitigation:
 * a company where one of five has been contacted must not read as worked, or
 * the other four have been hidden by the act of tidying up.
 */
export function groupSummary(group: LeadGroup): string {
  if (group.kind === 'lone') {
    return '';
  }
  const people = `${String(group.leads.length)} leads`;
  if (group.untouched === 0) {
    return `${people} · all worked`;
  }
  if (group.worked === 0) {
    return `${people} · none touched yet`;
  }
  return `${people} · ${String(group.worked)} worked, ${String(group.untouched)} untouched`;
}

/** True when a collapsed group would hide somebody nobody has contacted. */
export function hidesUntouched(group: LeadGroup): boolean {
  return group.kind === 'company' && group.untouched > 0;
}

// ── Remembering the toggle ─────────────────────────────────────────────────

/**
 * Its own key, and OFF unless this person turned it on.
 *
 * Off by default is the decision, not an implementation detail: the list a new
 * person opens shows every lead as its own row. Storing the choice per browser
 * means somebody who works the backlog this way does not re-tick it every
 * morning, and somebody who never touches it never sees it.
 *
 * Guarded reads and writes, like the drawer's folds: a browser set to block
 * site data throws on ACCESS rather than returning nothing, and a leads table
 * that will not render because it could not remember a toggle would be a worse
 * bug than the one being solved.
 */
const STORAGE_KEY = 'tap.leads.groupByCompany';

export function readGroupByCompany(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'on';
  } catch {
    return false;
  }
}

export function writeGroupByCompany(on: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, on ? 'on' : 'off');
  } catch {
    // The toggle works either way; only the memory of it is lost.
  }
}
